// Player names, not video names: they identify nothing and collide with each
// other, which is what turns a frame-title pass into a frame-title-unique fail.
export const PLACEHOLDER_TITLES = new Set([
  'video',
  'videos',
  'video title',
  'youtube',
  'youtube video',
  'youtube video player',
  'vimeo',
  'vimeo video player',
  'video player',
  'embedded video',
  'embedded content',
  'iframe',
  'untitled',
])

export const VIDEO_FALLBACK = 'Video'

export function isPlaceholderTitle(title?: string | null): boolean {
  const normalized = (title ?? '').replace(/\s+/g, ' ').trim().toLowerCase()
  return normalized === '' || PLACEHOLDER_TITLES.has(normalized)
}

export function firstUsableTitle(candidates: Array<string | null | undefined>): string | null {
  for (const candidate of candidates) {
    const trimmed = (candidate ?? '').replace(/\s+/g, ' ').trim()
    if (trimmed && !isPlaceholderTitle(trimmed))
      return trimmed
  }
  return null
}

/** Suffix until unused, so two unnamed videos on a page cannot share a name. */
export function uniqueTitle(base: string, used: Set<string>): string {
  const key = (value: string) => value.toLowerCase()
  if (!used.has(key(base))) {
    used.add(key(base))
    return base
  }
  for (let n = 2; ; n++) {
    const candidate = `${base} ${n}`
    if (!used.has(key(candidate))) {
      used.add(key(candidate))
      return candidate
    }
  }
}

// anonymousKey is required, not defaulted: a frame with no src and no candidates
// would otherwise key on a shared literal and reuse another frame's name.
export function allocateVideoTitle(
  store: Record<string, string>,
  candidates: Array<string | null | undefined>,
  src: string | null | undefined,
  anonymousKey: string,
): string {
  const named = candidates.map(c => (c ?? '').replace(/\s+/g, ' ').trim()).filter(Boolean)
  const key = (src ?? '').trim() || named.join('|') || anonymousKey

  const existing = store[key]
  if (existing)
    return existing

  const used = new Set(Object.values(store).map(name => name.toLowerCase()))
  const name = uniqueTitle(firstUsableTitle(named) ?? VIDEO_FALLBACK, used)
  store[key] = name
  return name
}
